/**
 * 解析の履歴と解析時の記録 (docs/27 §16)。
 *
 * - 1 回の解析ごとに `districts/{districtId}/posts/{postId}/analysisRuns/{runId}` へ、保存した結果・
 *   解析時の設定の参照・AI に送ったプロンプトの参照を残す。投稿本体の `aiAnalysis` は「今表示している結果」。
 * - 再解析の直前に、記録の無い既存の結果を「再解析前の結果」として写してから上書きする。
 * - プロンプトの文面・カテゴリマスタ・出力フォーマット・出力の型 (JSON スキーマ) は、中身の指紋 (sha256) を
 *   ID にした `districts/{districtId}/analysisSnapshots/{snapshotId}` に地区ごとに 1 回だけ保存し、記録からは
 *   ID で参照する。地区の下に置くのは、プロンプトに地区の上書きの文が入り、担当外の地区管理者に見せないため
 *   (docs/13 の「地区管理者は自地区のみ」)。
 *
 * 本ファイルは型と pure な補助関数のみ (指紋の計算と書込みは functions 側)。
 */
export const ANALYSIS_RUNS_COLLECTION = 'analysisRuns';
export const ANALYSIS_SNAPSHOTS_COLLECTION = 'analysisSnapshots';
/** スナップショットのコレクションのパス (地区ごと)。 */
export function analysisSnapshotsCollectionPath(districtId) {
    return `districts/${districtId}/${ANALYSIS_SNAPSHOTS_COLLECTION}`;
}
export const ANALYSIS_RUN_SCHEMA_VERSION = 1;
export const ANALYSIS_SNAPSHOT_SCHEMA_VERSION = 1;
/** 従来方式の机上調査のパイプライン名 (新方式は `STREET_SURVEY_PIPELINE_ID`)。 */
export const STREET_SURVEY_LEGACY_PIPELINE_ID = 'street-survey-legacy';
/** スナップショット 1 件の中身の上限 (Firestore の 1 ドキュメント 1MiB に余裕を残す)。 */
export const ANALYSIS_SNAPSHOT_MAX_BYTES = 900_000;
/** 画面表示用の名前 (内部キーを出さない)。 */
export const ANALYSIS_RUN_ORIGIN_LABELS = {
    analysis: '解析',
    'previous-result': '再解析前の結果',
};
/** 調査 (解析の種類) の識別子。出力フォーマット ID → 解析設定 ID → null。 */
export function resolveAnalysisSurveyKey(input) {
    const outputFormatId = input.outputFormatId?.trim();
    if (outputFormatId)
        return outputFormatId;
    const settingsId = input.settingsId?.trim();
    return settingsId || null;
}
/**
 * 再解析の前に、既存の結果を「再解析前の結果」として写すべきか。解析済みで、まだ記録を持たない
 * (`aiAnalysis.runId` が無い) ときだけ写す (記録を持つ結果は、その解析時に記録済み)。
 */
export function shouldArchivePreviousAnalysis(aiAnalysis) {
    if (!aiAnalysis || typeof aiAnalysis !== 'object')
        return false;
    const a = aiAnalysis;
    if (a.status !== 'done')
        return false;
    return typeof a.runId !== 'string' || a.runId.length === 0;
}
function isTimestampLike(value) {
    return typeof value.toDate === 'function';
}
/**
 * 指紋を取るための正規化 JSON。オブジェクトのキーを並べ替え、undefined を除き、日時 (Date / Firestore の
 * Timestamp) は ISO 文字列にする。同じ中身なら読込元や並び順が違っても同じ文字列になる。
 */
export function stableStringify(value) {
    return JSON.stringify(normalizeForFingerprint(value));
}
function normalizeForFingerprint(value) {
    if (value === null || value === undefined)
        return null;
    if (value instanceof Date)
        return value.toISOString();
    if (Array.isArray(value))
        return value.map((v) => (v === undefined ? null : normalizeForFingerprint(v)));
    if (typeof value === 'object') {
        if (isTimestampLike(value))
            return value.toDate().toISOString();
        const out = {};
        for (const key of Object.keys(value).sort()) {
            const v = value[key];
            if (v === undefined)
                continue;
            out[key] = normalizeForFingerprint(v);
        }
        return out;
    }
    if (typeof value === 'number' && !Number.isFinite(value))
        return null;
    return value;
}
//# sourceMappingURL=analysisRuns.js.map